import { useQuery } from '@tanstack/react-query';
import { get } from '../../../lib/api';

export const useReportsOverview = ({ campaignId }) => {
  return useQuery(
    ['reportsOverview', campaignId],
    () =>
      get('/api/client_admin/campaigns/reports/overview', {
        campaign_id: campaignId,
      }),
    { keepPreviousData: true },
  );
};

export const useReportsRating = ({ campaignId }) => {
  const {
    data = {
      data: {
        averageRating: 0,
        totalResponses: 0,
        showRating: false,
      },
    },
    isLoading,
  } = useQuery(
    ['reportsRating', campaignId],
    () =>
      get('/api/client_admin/campaigns/reports/rating', {
        campaign_id: campaignId,
      }),
    { keepPreviousData: true },
  );

  const {
    data: { averageRating, totalResponses, showRating },
  } = data;

  return { averageRating, totalResponses, showRating, isLoading };
};

export const useReportsRatingsList = ({ campaignId }) => {
  const { data = { data: [] }, isLoading } = useQuery(
    ['reportsRatingsList', campaignId],
    () =>
      get('/api/client_admin/campaign_user_feedbacks', {
        campaign_id: campaignId,
      }),
    { keepPreviousData: true },
  );

  return { data, isLoading };
};

export const useReportsFeedbackPaginated = ({
  page,
  itemsPerPage,
  sortBy,
  sortDirection,
  tileStatus,
  campaignId,
}) => {
  const baseParams = {
    page,
    limit: itemsPerPage,
    tile_status: tileStatus,
    campaign_id: campaignId,
  };

  const params = sortBy
    ? { ...baseParams, sort_by: sortBy, sort_direction: sortDirection }
    : baseParams;

  return useQuery(
    [
      'reportsFeedback',
      page,
      itemsPerPage,
      sortBy,
      sortDirection,
      tileStatus,
      campaignId,
    ],
    () => get('/api/client_admin/campaigns/reports/feedback', params),
    { keepPreviousData: true },
  );
};

export const useReportsSurvey = ({
  page,
  itemsPerPage,
  showAll,
  campaignId,
  sortBy,
  sortDirection,
  tileStatus,
}) => {
  const payload = !showAll
    ? {
        page,
        limit: itemsPerPage,
        campaign_id: campaignId,
        sort_by: sortBy,
        sort_direction: sortDirection,
        tile_status: tileStatus,
      }
    : {
        campaign_id: campaignId,
        sort_by: sortBy,
        sort_direction: sortDirection,
        tile_status: tileStatus,
      };

  const { data = { data: [], pagy: {} }, isLoading } = useQuery(
    ['reportsSurvey', payload],
    () => get('/api/client_admin/campaigns/reports/survey', payload),
    { keepPreviousData: true },
  );

  return { data, isLoading };
};

export const useReportsFeedbackAll = ({
  sortBy,
  sortDirection,
  campaignId,
  tileStatus,
}) =>
  useQuery(
    ['reportsFeedback', sortBy, sortDirection, campaignId, tileStatus],
    () =>
      get('/api/client_admin/campaigns/reports/feedback', {
        sort_by: sortBy,
        sort_direction: sortDirection,
        campaign_id: campaignId,
        tile_status: tileStatus,
      }),
  );

export const useReportsQuiz = ({
  page,
  itemsPerPage,
  showAll,
  campaignId,
  tileStatus,
  sortBy,
  sortDirection,
}) => {
  const payload = !showAll
    ? {
        page,
        limit: itemsPerPage,
        campaign_id: campaignId,
        sort_by: sortBy,
        sort_direction: sortDirection,
        tile_status: tileStatus,
      }
    : {
        campaign_id: campaignId,
        sort_by: sortBy,
        sort_direction: sortDirection,
        tile_status: tileStatus,
      };

  const { data = { data: [], pagy: {} }, isLoading } = useQuery(
    ['reportsQuiz', payload],
    () =>
      get('/api/client_admin/campaigns/reports/wrong_tile_completion', payload),
    { keepPreviousData: true },
  );

  return { data, isLoading };
};

const defaultBroadcastsSummaryData = {
  totalSent: 0,
  audienceReached: 0,
  uniqueOpens: 0,
  linkClicks: 0,
  emailsSent: 0,
  smsSent: 0,
  recentBroadcasts: [],
  linkTrackingDisabled: false,
};

export const useReportsBroadcastsSummary = ({ startDate, endDate } = {}) => {
  const { data, isLoading } = useQuery(
    ['reportsBroadcastsSummary', startDate, endDate],
    () =>
      get('/api/client_admin/campaigns/reports/broadcasts_summary', {
        start_date: startDate,
        end_date: endDate,
      }),
  );

  return {
    data: data?.data ?? defaultBroadcastsSummaryData,
    isLoading,
  };
};

const defaultAiSummaryData = {
  totalConversations: 0,
  totalMessages: 0,
  uniqueUsers: 0,
  avgMessagesPerConversation: 0,
  conversationsByType: { sms: 0, chat: 0 },
  feedbackBreakdown: { positive: 0, negative: 0, unclassified: 0 },
};

export const useReportsAiSummary = ({ campaignId, startDate, endDate }) => {
  const { data, isLoading } = useQuery(
    ['reportsAiSummary', campaignId, startDate, endDate],
    () =>
      get('/api/client_admin/campaigns/reports/ai_summary', {
        campaign_id: campaignId,
        start_date: startDate,
        end_date: endDate,
      }),
  );

  return {
    data: data?.data ?? defaultAiSummaryData,
    isLoading,
  };
};

const defaultConversationTrendsData = {
  series: [],
  totals: { conversations: 0, messages: 0 },
};

export const useReportsConversationTrends = ({
  campaignId,
  startDate,
  endDate,
}) => {
  const { data, isLoading } = useQuery(
    ['reportsConversationTrends', campaignId, startDate, endDate],
    () =>
      get('/api/client_admin/campaigns/reports/conversation_trends', {
        campaign_id: campaignId,
        start_date: startDate,
        end_date: endDate,
      }),
  );

  return {
    data: data?.data ?? defaultConversationTrendsData,
    isLoading,
  };
};

const defaultPeakUsageHoursData = {
  hours: [],
  peakHour: 0,
  peakCount: 0,
};

export const useReportsPeakUsageHours = ({
  campaignId,
  startDate,
  endDate,
}) => {
  const { data, isLoading } = useQuery(
    ['reportsPeakUsageHours', campaignId, startDate, endDate],
    () =>
      get('/api/client_admin/campaigns/reports/peak_usage_hours', {
        campaign_id: campaignId,
        start_date: startDate,
        end_date: endDate,
      }),
  );

  return {
    data: data?.data ?? defaultPeakUsageHoursData,
    isLoading,
  };
};
